
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import Video from 'plaid-threads/Icons/Video';
import External from 'plaid-threads/Icons/External';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: "Access users' financial information for loan underwriting"
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Callout = makeShortcode("Callout");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Assets'}>{metadata.description}</Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Assets'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/assets/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='/docs/quickstart' />
      <Actions.SmallCard icon={<External mdxType="External" />} title='Demo' link='https://plaid.coastdemo.com/share/66fb0a180582208ffa82103e?zoom=100' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <Callout mdxType="Callout">
      <p>{`Most new customers should use `}<a parentName="p" {...{
          "href": "/docs/check"
        }}>{`Consumer Report by Plaid Check`}</a>{` instead of Assets. Consumer Report is an FCRA-compliant product providing underwriting scores and insights, as well as Day 1 Certainty compatible Asset Verification reports for mortgage lending. Assets is currently recommended only for use cases not supported by Consumer Report, such as underwriting outside the US. `}</p>
    </Callout>
    <p>{`Assets data is used to determine whether a user has enough assets to qualify for a loan. Plaid's Assets product allows you to access a user's Asset Report via the `}<inlineCode parentName="p">{`/asset_report/get`}</inlineCode>{` (JSON) and `}<inlineCode parentName="p">{`/asset_report/pdf/get`}</inlineCode>{` (PDF) endpoints. Asset Reports are created on a per-user basis and include a snapshot of information about a user's identity, transaction history, account balances, and more.`}</p>
    <YouTubeEmbed videoId='98Lq6Fb-bwo' alignment='full' title='Plaid Assets in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Assets works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <p>{`For a fuller view of a borrower's financial situation, Assets can be used with `}<a parentName="p" {...{
        "href": "/docs/income"
      }}>{`Bank Income`}</a>{`, which provides user-permissioned income streams derived from deposit activity. `}</p>
    <p>{`If you need exact copies of a user's statements, see `}<a parentName="p" {...{
        "href": "/docs/statements"
      }}>{`Statements`}</a>{`.`}</p>
    <h4 {...{
      "id": "no-code-integration-with-the-credit-dashboard"
    }}>{`No-code integration with the Credit Dashboard`}</h4>
    <Callout mdxType="Callout">
      <p>{`The Credit Dashboard is currently in beta. To request access, contact your account manager. To use the Credit Dashboard for Assets, you must not be using any `}<a parentName="p" {...{
          "href": "/docs/check"
        }}>{`Plaid Check`}</a>{` products.`}</p>
    </Callout>
    <p>{`Assets data is available via a no-code integration through the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/credit"
      }}>{`Credit Dashboard`}</a>{`. You can fill out a form with basic information about your end user, such as their name and email address, and indicate how much history you would like to collect and whether you would like to enable `}<a parentName="p" {...{
        "href": "/docs/income/bank-income/"
      }}>{`Bank Income`}</a>{` for the session as well. Plaid will then generate a hyperlink that can be used to launch a Plaid-hosted Link session. Plaid can email the link directly to your user (Production only, not available in Sandbox), or you can send it to them yourself. `}</p>
    <p>{`After the end user successfully completes the Link session, their data will be available in the Dashboard for you to view, archive, or delete. The data shown will be the same data returned by a regular Assets call, in a user-friendly web-based session.`}</p>
    <Image src='/assets/img/docs/assets-dashboard-view.png' alt="Plaid Assets Dashboard: Overview tab with profile, income, and connected accounts info. Shows user's reference ID, income, and bank accounts." expandable={true} mdxType="Image" />
    <p>{`This integration is also compatible with Income, and you can enable Assets and Income in the same session.`}</p>
    <p>{`Once you are enabled for the Credit Dashboard, all new sessions going forward created via the Dashboard will be displayed in the Dashboard. Sessions created via the API will `}<em parentName="p">{`not`}</em>{` be displayed in the Dashboard for new customers.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`Data from Link sessions created via the Credit Dashboard cannot be accessed via the Plaid API. If you require programmatic access to data, use the `}<a parentName="p" {...{
          "href": "#integration-overview"
        }}>{`code-based integration flow`}</a>{` instead.`}</p>
    </Callout>
    <h4 {...{
      "id": "integration-overview"
    }}>{`Integration overview`}</h4>
    <p>{`There are a few steps you will need to take to obtain an Asset Report for a particular user.`}</p>
    <Callout title="Linking multiple institutions in one session" mdxType="Callout">
      <p>{`Since Asset Reports combine data from all of a user's linked institutions, you can use `}<a parentName="p" {...{
          "href": "/docs/link/multi-item-link"
        }}>{`Multi-Item Link`}</a>{` to let users link multiple accounts in a single Link session.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`First, using the Link flow, `}<a parentName="p" {...{
            "href": "/docs/api/items/#token-exchange-flow"
          }}>{`create Items`}</a>{` for each of the user's financial institutions; in doing so, you will obtain an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` for each institution. You can use these tokens to obtain an Asset Report as long as the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` was generated with `}<inlineCode parentName="p">{`assets`}</inlineCode>{` included in Link's `}<inlineCode parentName="p">{`products`}</inlineCode>{` array. With Assets, the user, upon using Link to authenticate with their financial institution, will need to provide consent for Plaid to access account balances, account holder information, and transaction history for that institution. Should the user revoke access via `}<a parentName="p" {...{
            "href": "https://my.plaid.com/"
          }}>{`my.plaid.com`}</a>{`, Plaid will notify you via a `}<a parentName="p" {...{
            "href": "/docs/api/items/#user_permission_revoked"
          }}>{`webhook`}</a>{`.`}</p>
        <ul parentName="li">
          <li parentName="ul">{`If your use case involves a user linking Items at multiple institutions, consider using `}<a parentName="li" {...{
              "href": "/docs/link/multi-item-link"
            }}>{`Multi-Item Link`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once you have the required `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`s, call the `}<inlineCode parentName="p">{`/asset_report/create`}</inlineCode>{` endpoint to create an Asset Report. Note that Asset Reports are not created instantly. When the Asset Report is ready, you will be notified via a `}<a parentName="p" {...{
            "href": "/docs/api/products/assets/#product_ready"
          }}><inlineCode parentName="a">{`PRODUCT_READY`}</inlineCode></a>{` webhook. If low latency is important to your use case, you can call `}<inlineCode parentName="p">{`/asset_report/create`}</inlineCode>{` with `}<inlineCode parentName="p">{`options.add_ons`}</inlineCode>{` set to `}<inlineCode parentName="p">{`["fast_assets"]`}</inlineCode>{`. This will cause Plaid to create two versions of the Asset Report: one with only current and available balance and identity information, and then later on the complete Asset Report. You will receive separate webhooks for each version of the Asset Report.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`After receiving the `}<a parentName="p" {...{
            "href": "/docs/api/products/assets/#product_ready"
          }}><inlineCode parentName="a">{`PRODUCT_READY`}</inlineCode></a>{` webhook, you can retrieve an Asset Report in JSON format by calling the `}<inlineCode parentName="p">{`/asset_report/get`}</inlineCode>{` endpoint. To retrieve an Asset Report in PDF format, call the `}<inlineCode parentName="p">{`/asset_report/pdf/get`}</inlineCode>{` endpoint. To retrieve an Asset Report with cleaned and categorized transactions as well as additional information about merchants and locations in JSON format, call the `}<inlineCode parentName="p">{`/asset_report/get`}</inlineCode>{` endpoint with `}<inlineCode parentName="p">{`include_insights=true`}</inlineCode>{`. If you enabled Fast Assets when calling `}<inlineCode parentName="p">{`/asset_report/create`}</inlineCode>{`, you can request the Fast version of the Asset Report by calling `}<inlineCode parentName="p">{`/asset_report/get`}</inlineCode>{` with `}<inlineCode parentName="p">{`fast_report=true`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Sometimes, third-party auditors need to see Asset Reports to audit lenders' decision-making processes. To provide an Audit Copy of an Asset Report to a requesting third party, call the `}<inlineCode parentName="p">{`/asset_report/audit_copy/create`}</inlineCode>{` endpoint and provide the auditor with an `}<inlineCode parentName="p">{`audit_copy_token`}</inlineCode>{`. The auditor needs to be integrated with Plaid in order to view Audit Copies. Currently, Fannie Mae, Freddie Mac, and Ocrolus are the only auditors integrated with Plaid.`}</p>
      </li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample asset report",
        "#Sample": true,
        "asset": true,
        "report": true
      }}>{`{
    "report": {
        "asset_report_id": "ebb8f490-8f45-4c93-a6c3-5801bf92c3ff",
        "client_report_id": null,
        "date_generated": "2026-08-04T07:20:25Z",
        "days_requested": 2,
        "items": [
            {
                "accounts": [
                    {
                        "account_id": "1Gd3X8NmgLFVn47RorabTJ7Bvy8vBqfpaG5Ky",
                        "balances": {
                            "available": null,
                            "current": 320.76,
                            "iso_currency_code": "USD",
                            "limit": null,
                            "unofficial_currency_code": null
                        },
                        "days_available": 0,
                        "historical_balances": [],
                        "investments": {
                            "holdings": [
                                {
                                    "account_id": "1Gd3X8NmgLFVn47RorabTJ7Bvy8vBqfpaG5Ky",
                                    "security_id": "PGRW123456789",
                                    "ticker_symbol": "PGRW",
                                    "institution_price": 32.076,
                                    "institution_price_as_of": "2026-08-04",
                                    "institution_value": 320.76,
                                    "quantity": 10,
                                    "iso_currency_code": "USD",
                                    "unofficial_currency_code": null,
                                    "tax_lots": [
                                        {
                                            "institution_lot_id": "1",
                                            "original_purchase_datetime": "2022-05-10T00:00:00Z",
                                            "quantity": 10,
                                            "purchase_price": 23,
                                            "cost_basis": 230,
                                            "current_value": 320.76,
                                            "position_type": "LONG"
                                        }
                                    ]
                                }
                            ],
                            "securities": [
                                {
                                    "security_id": "PGRW123456789",
                                    "name": "Plaid Growth Fund",
                                    "ticker_symbol": "PGRW",
                                    "type": "mutual fund"
                                }
                            ],
                            "transactions": []
                        },
                        "mask": "5555",
                        "name": "Plaid IRA",
                        "official_name": null,
                        "owners": [
                            {
                                "addresses": [
                                    {
                                        "data": {
                                            "city": "Malakoff",
                                            "country": "US",
                                            "postal_code": "14236",
                                            "region": "NY",
                                            "street": "2992 Cameron Road"
                                        },
                                        "primary": true
                                    }
                                ],
                                "emails": [
                                    {
                                        "data": "accountholder0@example.com",
                                        "primary": true,
                                        "type": "primary"
                                    }
                                ],
                                "names": [
                                    "Alberta Bobbeth Charleson"
                                ],
                                "phone_numbers": [
                                    {
                                        "data": "1112225555",
                                        "primary": false,
                                        "type": "mobile"
                                    }
                                ]
                            }
                        ],
                        "ownership_type": null,
                        "subtype": "ira",
                        "transactions": [],
                        "type": "investment"
                    },
                    {
                        "account_id": "7B73zkZlnLfeWMvkAlxpslbR4PM4RECdaKgll",
                        "balances": {
                            "available": 100,
                            "current": 110,
                            "iso_currency_code": "USD",
                            "limit": null,
                            "unofficial_currency_code": null
                        },
                        "days_available": 2,
                        "historical_balances": [
                            {
                                "current": 110,
                                "date": "2026-08-03",
                                "iso_currency_code": "USD",
                                "unofficial_currency_code": null
                            },
                            {
                                "current": 116.33,
                                "date": "2026-08-02",
                                "iso_currency_code": "USD",
                                "unofficial_currency_code": null
                            }
                        ],
                        "mask": "0000",
                        "name": "Plaid Checking",
                        "official_name": "Plaid Gold Standard 0% Interest Checking",
                        "owners": [
                            {
                                "addresses": [
                                    {
                                        "data": {
                                            "city": "Malakoff",
                                            "country": "US",
                                            "postal_code": "14236",
                                            "region": "NY",
                                            "street": "2992 Cameron Road"
                                        },
                                        "primary": true
                                    },
                                    {
                                        "data": {
                                            "city": "San Matias",
                                            "country": "US",
                                            "postal_code": "93405-2255",
                                            "region": "CA",
                                            "street": "2493 Leisure Lane"
                                        },
                                        "primary": false
                                    }
                                ],
                                "emails": [
                                    {
                                        "data": "accountholder0@example.com",
                                        "primary": true,
                                        "type": "primary"
                                    }
                                ],
                                "names": [
                                    "Alberta Bobbeth Charleson"
                                ],
                                "phone_numbers": [
                                    {
                                        "data": "1112223333",
                                        "primary": false,
                                        "type": "home"
                                    }
                                ]
                            }
                        ],
                        "ownership_type": null,
                        "subtype": "checking",
                        "transactions": [
                            {
                                "account_id": "7B73zkZlnLfeWMvkAlxpslbR4PM4RECdaKgll",
                                "amount": 6.33,
                                "date": "2026-08-03",
                                "iso_currency_code": "USD",
                                "original_description": "Uber 072515 SF**POOL**",
                                "pending": false,
                                "transaction_id": "mqdZ1xRbjau73pER6NrPSZQXZKQdN8CgLKrXA",
                                "unofficial_currency_code": null
                            }
                        ],
                        "type": "depository"
                    }
                ],
                "date_last_updated": "2026-08-04T07:16:21Z",
                "institution_id": "ins_3",
                "institution_name": "Chase",
                "item_id": "KEv98XqZ7yudGRpDqaxJsZ3PKDGzR8CRM13nr"
            }
        ],
        "user": {
            "client_user_id": null,
            "email": null,
            "first_name": null,
            "last_name": null,
            "middle_name": null,
            "phone_number": null,
            "ssn": null
        }
    },
    "request_id": "AYRjla7HgW4nKM9",
    "warnings": []
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`report`}</inlineCode>{` field of the above object contains the body of the Asset Report. This field consists of several subfields that contain information about the date and time the Asset Report was created; data submitted about the user; and information about Items, containing the user's historical balances, identity information, and more for each of the user's financial institutions. For a full explanation of the `}<inlineCode parentName="p">{`report`}</inlineCode>{` field and its subfields, consult the `}<a parentName="p" {...{
        "href": "/docs/api/products/assets/"
      }}>{`API Reference`}</a>{`.`}</p>
    <p>{`For investment-type accounts, the account's `}<inlineCode parentName="p">{`investments`}</inlineCode>{` object contains the holdings, securities, and investment transactions for that account. Each holding includes a `}<inlineCode parentName="p">{`tax_lots`}</inlineCode>{` array breaking out the cost basis, purchase price, and purchase date of each acquisition lot. The field is absent when lot-level data is not available for the holding, either because the institution does not provide it or because the report was generated before lot-level data was supported. For more details on investments data, see `}<a parentName="p" {...{
        "href": "/docs/investments"
      }}>{`Investments`}</a>{`.`}</p>
    <h4 {...{
      "id": "assets-webhooks"
    }}>{`Assets webhooks`}</h4>
    <p>{`When you create an Asset Report, Plaid aggregates account balances, account holder identity information, and transaction history for the duration specified. If you attempt to retrieve an Asset Report before the requested data has been collected, you'll receive a response with the HTTP status code 400 and a Plaid error code of `}<inlineCode parentName="p">{`PRODUCT_NOT_READY`}</inlineCode>{`.`}</p>
    <p>{`To remove the need for polling, Plaid sends a webhook to the URL you supplied when creating the Asset Report once the Report has been generated. If generation fails, the webhook will indicate why. For examples of webhooks corresponding to successful and unsuccessful Asset Report generation, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/assets/#webhooks"
      }}>{`API Reference for Assets webhooks`}</a>{`.`}</p>
    <h4 {...{
      "id": "refreshing-asset-reports"
    }}>{`Refreshing Asset Reports`}</h4>
    <p>{`Asset Reports can either be created or removed; they cannot be updated. To retrieve up-to-date information about users' transactions and balances, call the `}<inlineCode parentName="p">{`/asset_report/refresh`}</inlineCode>{` endpoint. This endpoint, when used, creates a new Asset Report based on the corresponding existing Asset Report.`}</p>
    <p>{`Note: Asset Report refresh is not supported in Canada.`}</p>
    <h4 {...{
      "id": "getting-an-asset-report-for-an-existing-item"
    }}>{`Getting an Asset Report for an existing Item`}</h4>
    <p>{`If your user has already connected their account to your application for a different product, you can add Assets to the existing Item via `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{`.`}</p>
    <p>{`To do so, in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request, populate the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` field with the access token for the Item, and set the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array to `}<inlineCode parentName="p">{`["assets"]`}</inlineCode>{`. If the user connected their account less than two years ago, they can bypass the Link credentials pane and complete just the Asset Report consent step. Otherwise, they will be prompted to complete the full Link flow.`}</p>
    <h4 {...{
      "id": "financial-account-matching"
    }}>{`Financial Account Matching`}</h4>
    <p>{`If you are already using `}<a parentName="p" {...{
        "href": "/docs/identity-verification"
      }}>{`Identity Verification`}</a>{`, you can enhance your verification process by enabling `}<a parentName="p" {...{
        "href": "/docs/identity-verification/#financial-account-matching"
      }}>{`Financial Account Matching`}</a>{` in your Identity Verification template.
This feature allows you to compare the data collected during KYC with the bank account data collected when creating an Asset Report.
To ensure accurate matching, it's important to maintain consistency in the `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` used for end users across all products. `}</p>
    <h4 {...{
      "id": "financial-insights-reports-uk-only"
    }}>{`Financial Insights Reports (UK only)`}</h4>
    <p>{`In the UK, the Financial Insights Report, including risk and affordability insights, is available as an optional add-on to Assets. Risk insights provide details on instances of potential risks such as loan disbursements, loan payments, gambling, bank penalties, and negative balances. Affordability insights provide insights based on spending / income ratios, considering factors such as essential vs. non-essential spend and other financial commitments.`}</p>
    <p>{`Like Asset Reports, Financial Insights Reports are available in both JSON and PDF format. `}<a parentName="p" {...{
        "href": "https://plaid.com/documents/sample-financial-insights-report.pdf"
      }}>{`View a sample Financial Insights Report (UK only) PDF`}</a>{`.`}</p>
    <p>{`To learn more about Financial Insights Reports or to request pricing details, `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{` or your Plaid account manager. `}</p>
    <h4 {...{
      "id": "testing-assets"
    }}>{`Testing Assets`}</h4>
    <p>{`Assets can be tested in `}<a parentName="p" {...{
        "href": "/docs/sandbox"
      }}>{`Sandbox`}</a>{` without any additional permissions and can be tested with live data using a `}<a parentName="p" {...{
        "href": "/docs/sandbox/#testing-with-live-data-using-a-trial-plan"
      }}>{`Trial plan`}</a>{`.`}</p>
    <p>{`To test configuration options beyond those offered by the default Sandbox user, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/#configuring-the-custom-user-account"
      }}>{`Configuring the custom user account`}</a>{`.`}</p>
    <h4 {...{
      "id": "attributes-library"
    }}>{`Attributes library`}</h4>
    <p>{`Plaid provides a GitHub `}<a parentName="p" {...{
        "href": "https://github.com/plaid/credit-attributes"
      }}>{`Credit attributes library`}</a>{` with Python scripts that can be used to derive various useful information from Asset Reports.`}</p>
    <h4 {...{
      "id": "assets-pricing"
    }}>{`Assets pricing`}</h4>
    <p>{`Assets is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#per-request-flexible-fee"
      }}>{`flexible fee model`}</a>{`; the cost will depend on the number of Items in the Asset Report (there is an additional cost for Asset Reports with more than 5 Items), as well as whether Additional History (over 61 days of history) has been requested for the report. Audit copies and PDF Asset Reports are billed based on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#per-request-flat-fee"
      }}>{`per-request model`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`To learn more about building with Assets, see `}<a parentName="p" {...{
        "href": "/docs/assets/add-to-app"
      }}>{`Create an Asset Report`}</a>{` and the `}<a parentName="p" {...{
        "href": "/docs/api/products/assets/"
      }}>{`API Reference`}</a>{`.`}</p>
    <p>{`For a more detailed overview of the steps involved in creating a full implementation of Plaid Assets, see the `}<a href="/documents/plaid-assets-solution-guide.pdf" target="_blank">{`Plaid Assets Solution Guide`}</a>{`.`}</p>
    <p>{`If you're ready to launch to Production, see the `}<a parentName="p" {...{
        "href": "/docs/launch-checklist/"
      }}>{`Launch checklist`}</a>{`.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle="See next steps to launch in Production" link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "no-code-integration-with-the-credit-dashboard",
        level: 4,
        title: "No-code integration with the Credit Dashboard",
      },
{
        id: "integration-overview",
        level: 4,
        title: "Integration overview",
      },
{
        id: "assets-webhooks",
        level: 4,
        title: "Assets webhooks",
      },
{
        id: "refreshing-asset-reports",
        level: 4,
        title: "Refreshing Asset Reports",
      },
{
        id: "getting-an-asset-report-for-an-existing-item",
        level: 4,
        title: "Getting an Asset Report for an existing Item",
      },
{
        id: "financial-account-matching",
        level: 4,
        title: "Financial Account Matching",
      },
{
        id: "financial-insights-reports-uk-only",
        level: 4,
        title: "Financial Insights Reports (UK only)",
      },
{
        id: "testing-assets",
        level: 4,
        title: "Testing Assets",
      },
{
        id: "attributes-library",
        level: 4,
        title: "Attributes library",
      },
{
        id: "assets-pricing",
        level: 4,
        title: "Assets pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
